import { useEffect } from 'react';

import { electronApis } from '@/features/electron-api';
import { isNewTabClick, tabStore } from '@/features/tabs';
import { inDesktop, inElectron } from '@/utils/capabilities';
import { openUntrustedUrl } from '@/utils/external-links';
import { navigate } from '@/utils/navigation-service';

interface ExternalLinkHandlerProps {
  children: React.ReactNode;
}

export function ExternalLinkHandler({ children }: ExternalLinkHandlerProps) {
  useEffect(
    () =>
      electronApis.system.listenForExternalLinkRequest(({ url }) => {
        openUntrustedUrl(url);
      }),
    []
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      const anchor = target.closest('a') as HTMLAnchorElement;

      if (
        e.defaultPrevented ||
        !anchor ||
        !anchor.href ||
        anchor.hasAttribute('download')
      ) {
        return;
      }

      // No Electron window may navigate away, Hummingbird included; the browser build keeps its default mailto handling
      if (anchor.href.startsWith('mailto:')) {
        if (!inElectron()) return;
        e.preventDefault();
        electronApis.system.openExternal(anchor.href);
        return;
      }

      if (!anchor.href.startsWith('http')) return;

      const internalChatMatch = anchor.href.match(
        /^https?:\/\/app\.littlebird\.ai\/chats\/([^/?#]+)/
      );

      if (internalChatMatch) {
        e.preventDefault();
        const chatId = internalChatMatch[1];
        const chatPath = `/chats/${chatId}`;
        if (isNewTabClick(e)) {
          tabStore.createTab({ path: chatPath });
          return;
        }
        navigate({ to: chatPath });
        return;
      }

      e.preventDefault();
      if (inDesktop()) {
        electronApis.system.openExternal(anchor.href);
      } else {
        window.open(anchor.href, '_blank', 'noopener,noreferrer');
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return <>{children}</>;
}
